import { useEffect, useRef } from "react";

/** Invoer via `beforeinput` die een nieuwe regel zou maken; een zoekveld is één regel. */
const NEWLINE_INPUT_TYPES = ['insertParagraph', 'insertLineBreak'];

/**
 * Een zoekterm op één regel: regelovergangen worden een spatie.
 *
 * @param {string} text
 * @returns {string}
 */
const toOneLine = (text) => text.replace(/[\r\n]+/g, ' ');

/**
 * Het zoekveld van de gedeelde tabel.
 *
 * Bewust geen <input> maar een bewerkbaar vak (contenteditable). De tabel staat
 * op 45 schermen binnen een ReadOnlyGuard, en die zet voor een lezer (rol
 * zonder bewerkrecht) alles uit met een <fieldset disabled>: élk <input> erin,
 * ook een zoekveld, en één element kan daar niet uit ontsnappen. Zoeken wijzigt
 * niets, dus een lezer hoort het te kunnen. Een bewerkbaar vak is geen
 * formulierveld en blijft daardoor werken.
 *
 * Wat een <input> vanzelf doet, doet dit vak hier zelf: één regel (Enter maakt
 * geen nieuwe regel, een geplakte regelovergang wordt een spatie), platte tekst
 * bij plakken (`plaintext-only`), de zoektoets op een telefoontoetsenbord, en
 * de placeholder als naam en als tekst in het lege vak (CSS, `data-placeholder`).
 *
 * `plaintext-only` kent Firefox pas sinds versie 136 (maart 2025); in een
 * oudere Firefox is het vak niet bewerkbaar. Bewust geen terugval: die versies
 * krijgen geen beveiligingsupdates meer.
 *
 * @param {object} props
 * @param {string} props.value - De huidige zoekterm
 * @param {(next: string) => void} props.onChange - Aangeroepen bij elke wijziging
 * @param {string} [props.placeholder] - Tekst in het lege veld, en de naam van het veld
 * @param {string} [props.className] - Extra class op de wikkel
 * @param {() => void} [props.onFocus] - Aangeroepen zodra het veld focus krijgt
 */
const TableSearch = ({
  value,
  onChange,
  placeholder = 'Zoeken op eerste kolom',
  className = '',
  onFocus,
}) => {
  /** @type {React.RefObject<HTMLDivElement>} */
  const fieldRef = useRef(null);
  const classes = ['common-table__search', className].filter(Boolean).join(' ');

  // De tekst staat in de DOM en niet in React-children: zou React hem
  // renderen, dan zet elke render de cursor terug naar het begin. Alleen als
  // de waarde van buitenaf verandert (de ouder leegt de zoekterm), schrijven
  // we hem hier over.
  useEffect(() => {
    const field = fieldRef.current;
    if (field && field.textContent !== (value || '')) field.textContent = value || '';
  }, [value]);

  // Een nieuwe regel weigeren we bij `beforeinput` en niet bij de toets
  // Enter: een Android-toetsenbord meldt Enter niet als 'Enter'. Native,
  // omdat React's onBeforeInput een ander event is zonder `inputType`.
  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return undefined;
    const refuseNewline = (event) => {
      if (NEWLINE_INPUT_TYPES.includes(event.inputType)) event.preventDefault();
    };
    field.addEventListener('beforeinput', refuseNewline);
    return () => field.removeEventListener('beforeinput', refuseNewline);
  }, []);

  /** @param {React.FormEvent<HTMLDivElement>} event */
  const handleInput = (event) => {
    const field = event.currentTarget;
    const nextValue = toOneLine(field.textContent || '');
    // Een leeggemaakt vak houdt soms een <br> over; dan toont :empty de
    // placeholder niet meer. Niet tijdens het samenstellen van een teken
    // (Japans, Chinees): dan zou het half samengestelde teken verdwijnen.
    if (nextValue === '' && field.innerHTML !== '' && !event.nativeEvent.isComposing) field.innerHTML = '';
    onChange(nextValue);
  };

  /**
   * Plakt de tekst op één regel. Al bij het plakken, niet pas in de zoekterm:
   * anders staat er in het vak iets anders dan waarop gezocht wordt.
   * `insertText` houdt de cursor op zijn plek en de stap in de
   * ongedaan-maakgeschiedenis; het vuurt zelf `input`, dus handleInput volgt.
   *
   * @param {React.ClipboardEvent<HTMLDivElement>} event
   */
  const handlePaste = (event) => {
    event.preventDefault();
    document.execCommand('insertText', false, toOneLine(event.clipboardData.getData('text/plain')));
  };

  /** @param {React.KeyboardEvent<HTMLDivElement>} event */
  const handleKeyDown = (event) => {
    // Tijdens het samenstellen van een teken bevestigt Enter dat teken.
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className={classes}>
      {/* Loepje ín het veld: het zoekveld is het enige element in de balk met
          een rand, en het icoon zegt wat het is zonder extra label. */}
      <svg className="common-table__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
      </svg>
      <div
        ref={fieldRef}
        className="common-table__search-input"
        role="searchbox"
        contentEditable="plaintext-only"
        spellCheck={false}
        inputMode="search"
        enterKeyHint="search"
        aria-label={placeholder}
        aria-placeholder={placeholder}
        data-placeholder={placeholder}
        onInput={handleInput}
        onPaste={handlePaste}
        onFocus={onFocus}
        onKeyDown={handleKeyDown}
        onKeyUp={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      />
    </div>
  );
};

export default TableSearch;
